import axios from 'axios';
// @ts-ignore - music-lyrics doesn't have TypeScript definitions
import musicLyrics from 'music-lyrics';
import { lyricsService } from './lyricsService';

/**
 * Spotify Service
 * 
 * IMPORTANT COMPLIANCE NOTICE:
 * 
 * This service does NOT stream or play audio content directly.
 * It only interacts with the official Spotify API to:
 * 1. Get metadata about currently playing tracks
 * 2. Retrieve timestamps for lyric synchronization
 * 3. Allow Premium users to control their Spotify playback (play/pause/seek)
 * 
 * All interactions comply with Spotify's Developer Terms:
 * - No audio content is streamed by SyncdIt
 * - Playback control features are only available to Premium subscribers
 * - All track data is properly attributed to Spotify
 * - User authentication tokens are securely stored
 * 
 * This service enhances the Spotify experience by providing synchronized lyrics
 * for music playing through the official Spotify app.
 */

// Add type declaration at the top of the file
declare global {
  interface Window {
    tokenRefreshTimer?: number;
  }
}

const CLIENT_ID = import.meta.env.VITE_SPOTIFY_CLIENT_ID;
// Dynamically select the redirect URI based on the current environment
const IS_PRODUCTION = window.location.hostname !== 'localhost';
const REDIRECT_URI = IS_PRODUCTION 
  ? 'https://syncdit.com/auth/callback'
  : 'https://localhost:5173/auth/callback';
const SPOTIFY_API_URL = 'https://api.spotify.com/v1';
const SPOTIFY_ACCOUNTS_URL = 'https://accounts.spotify.com/api/token';

// Debug logging
console.log('Spotify Service Initialized with:', {
  CLIENT_ID: CLIENT_ID ? 'Set' : 'Not set',
  REDIRECT_URI,
  IS_PRODUCTION,
  'Current hostname': window.location.hostname,
  'ENV Variables Available': Object.keys(import.meta.env).filter(key => key.startsWith('VITE_')),
});

if (!CLIENT_ID) {
  console.error('Missing required environment variables for login');
}

// Cache for lyrics to avoid repeated API calls
const lyricsCache = new Map();

// Track tracking variables
let currentTrackId: string | null = null;
let lastTrackCheckTime = 0;
let currentPlayerState: any = null;
let lastProgressMs = 0;
let progressCheckTime = Date.now();

// Define token refresh event for components to listen to
const TOKEN_EXPIRED_EVENT = 'spotify-token-expired';

// Define a constant for the progress difference threshold
const PROGRESS_DIFF_THRESHOLD_MS = 3000; // 3 seconds in milliseconds

// Token refresh state tracking
let isRefreshingToken = false;
let refreshTokenPromise: Promise<string | null> | null = null;
let lastToastTime = 0;
const TOAST_COOLDOWN_MS = 60000; // 1 minute between toasts

// PKCE Helper functions
function generateRandomString(length: number): string {
  const possible = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  const values = crypto.getRandomValues(new Uint8Array(length));
  return Array.from(values)
    .map(x => possible[x % possible.length])
    .join('');
}

async function generateCodeChallenge(codeVerifier: string): Promise<string> {
  const encoder = new TextEncoder();
  const data = encoder.encode(codeVerifier);
  const digest = await crypto.subtle.digest('SHA-256', data);
  
  return btoa(String.fromCharCode(...new Uint8Array(digest)))
    .replace(/=/g, '')
    .replace(/\+/g, '-')
    .replace(/\//g, '_');
}

// Helper function to check if current path is a player page that needs Spotify
function isPlayerPage(pathname: string): boolean {
  return pathname.includes('/player') || 
         pathname.includes('/auth/callback') || 
         pathname === '/';
}

export const spotifyService = {
  login: async () => {
    if (!CLIENT_ID) {
      console.error('Missing required environment variables for login');
      return;
    }

    try {
      // PKCE: Generate code verifier and challenge
      const codeVerifier = generateRandomString(64);
      const codeChallenge = await generateCodeChallenge(codeVerifier);
      
      // Store code verifier in localStorage to use it later when exchanging the code for a token
      localStorage.setItem('spotify_code_verifier', codeVerifier);
      
      const scope = 'user-read-playback-state user-modify-playback-state user-read-currently-playing';
      const authUrl = new URL('https://accounts.spotify.com/authorize');
      authUrl.searchParams.append('client_id', CLIENT_ID);
      authUrl.searchParams.append('response_type', 'code');
      authUrl.searchParams.append('redirect_uri', REDIRECT_URI);
      authUrl.searchParams.append('scope', scope);
      authUrl.searchParams.append('code_challenge_method', 'S256');
      authUrl.searchParams.append('code_challenge', codeChallenge);
      authUrl.searchParams.append('show_dialog', 'true');

      console.log('Redirecting to Spotify auth URL with PKCE:', authUrl.toString());
      window.location.href = authUrl.toString();
    } catch (error) {
      console.error('Error during login redirect:', error);
    }
  },

  handleCallback: async () => {
    try {
      console.log('Handling callback with URL:', window.location.href);
      
      // Check for authorization code in URL
      const urlParams = new URLSearchParams(window.location.search);
      const code = urlParams.get('code');
      const errorParam = urlParams.get('error');
      
      if (errorParam) {
        console.error('Spotify auth error:', errorParam);
        return null;
      }
      
      if (!code) {
        console.error('No code found in callback URL');
        return null;
      }
      
      // Get the code verifier we stored during login
      const codeVerifier = localStorage.getItem('spotify_code_verifier');
      if (!codeVerifier) {
        console.error('No code verifier found');
        return null;
      }
      
      // Exchange code for token
      const response = await axios.post(
        SPOTIFY_ACCOUNTS_URL,
        new URLSearchParams({
          client_id: CLIENT_ID,
          grant_type: 'authorization_code',
          code: code,
          redirect_uri: REDIRECT_URI,
          code_verifier: codeVerifier,
        }), 
        {
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
          },
        }
      );
      
      const { access_token, refresh_token, expires_in } = response.data;
      
      // Store tokens in localStorage
      localStorage.setItem('spotify_access_token', access_token);
      localStorage.setItem('spotify_refresh_token', refresh_token);
      
      // Calculate and store expiration time
      const expirationTime = Date.now() + (expires_in * 1000);
      localStorage.setItem('spotify_token_expiration', expirationTime.toString());
      
      console.log('Token exchange successful, expires in:', expires_in, 'seconds');
      return access_token;
    } catch (error) {
      console.error('Error handling callback:', error);
      return null;
    }
  },

  // Schedule a token refresh before the current token expires
  scheduleTokenRefresh: (expiresInSeconds: number) => {
    // If not on a player page, don't schedule token refresh
    if (!isPlayerPage(window.location.pathname)) {
      console.log('Not on player page, skipping token refresh scheduling');
      return;
    }
    
    // Clear any existing refresh timers
    if (window.tokenRefreshTimer) {
      clearTimeout(window.tokenRefreshTimer);
    }
    
    // Calculate when to refresh - 5 minutes before expiration or halfway through, whichever is smaller
    const refreshDelay = Math.min((expiresInSeconds - 300) * 1000, expiresInSeconds * 1000 * 0.5);
    
    // Only schedule if the delay is positive
    if (refreshDelay > 0) {
      console.log(`Scheduling token refresh in ${refreshDelay/1000} seconds (before token expires)`);
      
      window.tokenRefreshTimer = setTimeout(async () => {
        // Check again if we're on a player page when the timer executes
        if (!isPlayerPage(window.location.pathname)) {
          console.log('Not on player page when refresh timer executed, skipping token refresh');
          return;
        }
        
        console.log('Executing scheduled token refresh');
        await spotifyService.refreshToken();
      }, refreshDelay);
    } else {
      console.log('Token expires too soon to schedule refresh, refreshing immediately');
      // If token is about to expire, refresh immediately rather than waiting
      spotifyService.refreshToken();
    }
  },

  /**
   * Token refresh debounce mechanism
   */
  _isRefreshing: false,
  _refreshPromise: null,
  _refreshDebounceTimeoutId: null,
  _refreshDebounceDelay: 2000, // 2 seconds

  refreshToken: async (): Promise<string | null> => {
    // If not on a player page, don't attempt to refresh the token
    if (!isPlayerPage(window.location.pathname)) {
      console.log('Not on player page, skipping token refresh');
      return null;
    }
    
    // If we're already refreshing the token, return the existing promise
    if (isRefreshingToken && refreshTokenPromise) {
      console.log('Token refresh already in progress, reusing existing promise');
      return refreshTokenPromise;
    }
    
    // Start the token refresh process
    isRefreshingToken = true;
    refreshTokenPromise = (async () => {
      try {
        // Check if we've refreshed the token in the last hour
        const lastRefreshTime = localStorage.getItem('spotify_last_token_refresh');
        if (lastRefreshTime) {
          const oneHourAgo = Date.now() - (60 * 60 * 1000); // 1 hour in milliseconds
          const lastRefreshTimestamp = parseInt(lastRefreshTime);
          
          // If the token was refreshed less than an hour ago, use the existing token
          if (lastRefreshTimestamp > oneHourAgo) {
            console.log(`Token was refreshed ${Math.round((Date.now() - lastRefreshTimestamp) / 1000 / 60)} minutes ago, skipping refresh`);
            // Return the current access token
            const currentToken = localStorage.getItem('spotify_access_token');
            if (currentToken) {
              return currentToken;
            }
          }
        }
        
        const refreshToken = localStorage.getItem('spotify_refresh_token');
        if (!refreshToken) {
          console.error('No refresh token available');
          return null;
        }
        
        console.log('Refreshing access token with refresh token:', refreshToken.substring(0, 5) + '...');
        const response = await axios.post(
          SPOTIFY_ACCOUNTS_URL,
          new URLSearchParams({
            grant_type: 'refresh_token',
            refresh_token: refreshToken,
            client_id: CLIENT_ID,
          }),
          {
            headers: {
              'Content-Type': 'application/x-www-form-urlencoded',
            },
          }
        );
        
        const { access_token, refresh_token, expires_in } = response.data;
        
        // Update the access token
        localStorage.setItem('spotify_access_token', access_token);
        
        // Track when we last refreshed the token
        localStorage.setItem('spotify_last_token_refresh', Date.now().toString());
        
        // Update refresh token if provided (Spotify sometimes rotates refresh tokens)
        if (refresh_token) {
          console.log('Got new refresh token, updating storage');
          localStorage.setItem('spotify_refresh_token', refresh_token);
        }
        
        // Update expiration time
        const expirationTime = Date.now() + (expires_in * 1000);
        localStorage.setItem('spotify_token_expiration', expirationTime.toString());
        
        console.log('Token refreshed successfully, expires in:', expires_in, 'seconds');
        
        // Schedule the next refresh to occur just before expiration
        spotifyService.scheduleTokenRefresh(expires_in);
        
        return access_token;
      } catch (error: any) {
        console.error('Error refreshing token:', error?.response?.data || error);
        
        // Check if the error is because the refresh token is invalid/expired
        if (error?.response?.status === 400 && 
            (error?.response?.data?.error === 'invalid_grant' || 
             error?.response?.data?.error_description?.includes('refresh token'))) {
          console.error('Refresh token is invalid or expired. User needs to re-authenticate.');
          // Dispatch event to notify UI that user needs to re-login
          window.dispatchEvent(new CustomEvent(TOKEN_EXPIRED_EVENT));
        }
        
        // If refresh fails, we should clear all tokens and require re-authentication
        localStorage.removeItem('spotify_access_token');
        localStorage.removeItem('spotify_refresh_token');
        localStorage.removeItem('spotify_token_expiration');
        return null;
      } finally {
        // Reset refresh state variables when done
        isRefreshingToken = false;
        refreshTokenPromise = null;
      }
    })();
    
    return refreshTokenPromise;
  },

  // Check if the access token is expired
  isTokenExpired: (): boolean => {
    // If not on a player page, don't check token expiration
    if (!isPlayerPage(window.location.pathname)) {
      return false;
    }
    
    const expirationTimeStr = localStorage.getItem('spotify_token_expiration');
    
    if (!expirationTimeStr) {
      // If no expiration time is found, consider the token expired
      return true;
    }
    
    const expirationTime = parseInt(expirationTimeStr);
    const now = Date.now();
    
    return now >= expirationTime;
  },

  // Notify about expired token
  notifyTokenExpired: () => {
    console.log('Dispatching token expired event');
    window.dispatchEvent(new CustomEvent(TOKEN_EXPIRED_EVENT));
    return false;
  },

  // Handle API errors and check for token expiration
  handleApiError: async (error: any) => {
    // Check for 401 Unauthorized error with "The access token expired" message
    if (error.response && error.response.status === 401) {
      if (error.response.data?.error?.message === 'The access token expired') {
        console.log('Access token expired error detected, attempting to refresh...');
        
        // Try to refresh the token
        const newToken = await spotifyService.refreshToken();
        if (newToken) {
          console.log('Token refreshed successfully');
          return { refreshed: true, token: newToken };
        } else {
          console.log('Token refresh failed, notifying expiration');
          localStorage.removeItem('spotify_access_token');
          return { refreshed: false, token: null };
        }
      }
    }
    return { refreshed: false, token: null };
  },

  getCurrentTrack: async (accessToken: string) => {
    try {
      const startTime = performance.now();
      const response = await axios.get(`${SPOTIFY_API_URL}/me/player/currently-playing`, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });
      const endTime = performance.now();
      const apiLatency = Math.round(endTime - startTime);
      
      // Add the API latency to the response
      return {
        ...response.data,
        _apiLatency: apiLatency // Add API latency in milliseconds
      };
    } catch (error) {
      console.error('Error fetching current track:', error);
      
      // Check if this is a token expiration error and attempt to refresh
      const refreshResult = await spotifyService.handleApiError(error);
      if (refreshResult.refreshed) {
        console.log('Token refreshed after expiration, retrying getCurrentTrack');
        // Retry the request with the new token
        return spotifyService.getCurrentTrack(refreshResult.token);
      }
      
      return null;
    }
  },

  // Method to explicitly reset the current track
  resetCurrentTrack: () => {
    currentTrackId = null;
    currentPlayerState = null;
    lastTrackCheckTime = 0;
    lastProgressMs = 0;
  },

  getLyrics: async (accessToken: string, trackId: string) => {
    // Check token expiration before making the API call
    if (spotifyService.isTokenExpired()) {
      // Try to refresh the token instead of just notifying
      const newToken = await spotifyService.refreshToken();
      if (newToken) {
        console.log('Token refreshed, continuing with new token');
        accessToken = newToken;
      } else {
        return spotifyService.notifyTokenExpired();
      }
    }

    try {
      // First get the track details from Spotify
      const response = await axios.get(`${SPOTIFY_API_URL}/tracks/${trackId}`, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });
      
      const trackName = response.data.name;
      const artistName = response.data.artists[0].name;
      const albumName = response.data.album.name;
      const durationMs = response.data.duration_ms / 1000; // Convert to seconds for SyncdIt-Lyrics
      const searchQuery = `${trackName} ${artistName}`;
      
      // Check cache first
      const cacheKey = `${trackName}-${artistName}`;
      if (lyricsCache.has(cacheKey)) {
        console.log('Returning cached lyrics for:', cacheKey);
        return lyricsCache.get(cacheKey);
      }
      
      // Try to get lyrics from SyncdIt-Lyrics first (better quality, has synced lyrics)
      try {
        console.log('Searching for lyrics with SyncdIt-Lyrics:', searchQuery);
        const syncditLyricsResult = await lyricsService.getLyrics(
          artistName, 
          trackName, 
          albumName, 
          durationMs
        );
        
        if (syncditLyricsResult) {
          const result = {
            lyrics: syncditLyricsResult.lyrics,
            source: 'syncdit-lyrics',
            synced: syncditLyricsResult.synced,
            syncedLyrics: syncditLyricsResult.syncedLyrics
          };
          
          // Cache the result
          lyricsCache.set(cacheKey, result);
          return result;
        }
      } catch (lrcLibError) {
        console.error('Error fetching lyrics from SyncdIt-Lyrics:', lrcLibError);
      }
      
      // Fallback to music-lyrics if SyncdIt-Lyrics fails
      try {
        console.log('Falling back to music-lyrics:', searchQuery);
        const lyricResult = await musicLyrics.search(searchQuery);
        
        if (lyricResult) {
          const result = {
            lyrics: lyricResult,
            source: 'music-lyrics',
            synced: false
          };
          
          // Cache the result
          lyricsCache.set(cacheKey, result);
          return result;
        }
      } catch (lyricsError) {
        console.error('Error fetching lyrics from music-lyrics:', lyricsError);
      }
      
      // Fallback if all APIs fail
      const fallbackResult = {
        lyrics: `Searching for lyrics to "${trackName}" by ${artistName}...\n\nNo lyrics found. Try playing another song.`,
        source: 'fallback',
        synced: false
      };
      
      // Cache the fallback result
      lyricsCache.set(cacheKey, fallbackResult);
      return fallbackResult;
    } catch (error) {
      console.error('Error in getLyrics:', error);
      
      // Try to refresh token on error
      const refreshResult = await spotifyService.handleApiError(error);
      if (refreshResult.refreshed) {
        // Retry with new token
        return spotifyService.getLyrics(refreshResult.token, trackId);
      }
      
      return {
        lyrics: "Error fetching lyrics. Please try again later.",
        source: 'error',
        synced: false
      };
    }
  },

  getQueue: async (accessToken: string) => {
    // Check token expiration before making the API call
    if (spotifyService.isTokenExpired()) {
      // Try to refresh the token instead of just notifying
      const newToken = await spotifyService.refreshToken();
      if (newToken) {
        console.log('Token refreshed, continuing with new token');
        accessToken = newToken;
      } else {
        return spotifyService.notifyTokenExpired();
      }
    }
    
    try {
      const response = await axios.get(`${SPOTIFY_API_URL}/me/player/queue`, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching queue:', error);
      
      // Try to refresh token on error
      const refreshResult = await spotifyService.handleApiError(error);
      if (refreshResult.refreshed) {
        // Retry with new token
        return spotifyService.getQueue(refreshResult.token);
      }
      
      return null;
    }
  },
  
  // Seek to a specific position in the currently playing track
  seekToPosition: async (accessToken: string, positionMs: number) => {
    // Check token expiration before making the API call
    if (spotifyService.isTokenExpired()) {
      // Try to refresh the token instead of just notifying
      const newToken = await spotifyService.refreshToken();
      if (newToken) {
        console.log('Token refreshed, continuing with new token');
        accessToken = newToken;
      } else {
        return spotifyService.notifyTokenExpired();
      }
    }
    
    try {
      console.log(`Seeking to position ${positionMs}ms in current track`);
      
      const response = await axios.put(
        `${SPOTIFY_API_URL}/me/player/seek?position_ms=${positionMs}`,
        {},  // Empty body for PUT request
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );
      
      // Return true for successful seek
      return response.status === 204;
    } catch (error) {
      console.error('Error seeking to position:', error);
      
      // Try to refresh token on error
      const refreshResult = await spotifyService.handleApiError(error);
      if (refreshResult.refreshed) {
        // Retry with new token
        return spotifyService.seekToPosition(refreshResult.token, positionMs);
      }
      
      return false;
    }
  },
  
  // Return the token expired event name
  getTokenExpiredEvent: () => TOKEN_EXPIRED_EVENT,

  // Check if it's been too soon to show another toast notification
  shouldShowToast: (): boolean => {
    const now = Date.now();
    if (now - lastToastTime > TOAST_COOLDOWN_MS) {
      lastToastTime = now;
      return true;
    }
    console.log('Toast cooldown active, suppressing notification');
    return false;
  }
}; 